import { useEffect, useState } from 'react';
import { Main } from 'src/Layouts';
import { YouInvest, MorningStar, EQUITY, FUND, ETF, CEF } from 'src/Api';
import { sendCacheHeaders } from 'src/Utils';
import { ResultsTable } from './Components/Card/';
import { Screener, Card } from "@ajbellbusinessolution/fe-components";
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';

const FUNDS = 'funds';
const ETFS = 'etf';
const SHARES = 'shares';
const TRUSTS = 'trusts';

const screenerStateSessionKey = 'screenerComponentState';
const screenerHeaderTabs = [
    {
        title: 'Funds',
        link: '/market-research/screener/funds'
    },
    {
        title: 'ETF',
        link: '/market-research/screener/etf'
    },
    {
        title: 'Shares',
        link: '/market-research/screener/shares'
    },
    {
        title: 'Investment Trusts',
        screenTypeMatch: 'Investment Trust',
        link: '/market-research/screener/trusts'
    }
];

const displayTopBox = (screenerType) => {

    switch (screenerType ?? 'funds') {

        case 'Funds':
            return <div className="ajb-grid-row">
                <h1 className="h2">Fund screener</h1>
                <p className="screener-header-sub-text">
                    Sift through thousands of funds. Find exactly the right one for you.
                </p>
                <p className="screener-header-text">
                    Looking for a fund? Our fund screener lets you filter every fund we offer by performance, 
                    yield, risk rating and more. You can also find a fund managed or selected by us by searching 
                    just our AJ Bell funds or AJ Bell Favourite funds list.
                </p>
            </div>;
        case 'ETF':
            return <div className="ajb-grid-row">
                <h1 className="h2">ETF screener</h1>
                <p className="screener-header-sub-text">
                    Explore our wide range of exchange-traded funds, and find the right one for you.
                </p>
                <p className="screener-header-text">
                    An exchange-traded fund (ETF) usually tracks a specific market, theme or region. Our ETF 
                    screener lets you filter by sector, region, fund provider – and more – to find the right ETF 
                    for your portfolio.&nbsp;
                    <a id="etf-header-link" target="_blank" href="/our-services/investment-options/exchange-traded-funds">
                        Learn more about ETFs
                    </a>
                </p>
            </div>;
        case 'Shares':
            return <div className="ajb-grid-row">
                <h1 className="h2">Share screener</h1>
                <p className="screener-header-sub-text">
                    Find just the right stock for you using our screener.
                </p>
                <p className="screener-header-text">
                    Stuck for which share to pick? Narrow your net with our screener. You can filter by sector, 
                    location, performance and price, and search for risers or fallers, growth or value stocks, 
                    across both the UK and US markets.
                </p>
            </div>;
        case 'Investment Trust':
            return <div className="ajb-grid-row">
                <h1 className="h2">Investment trust screener</h1>
                <p className="screener-header-sub-text">
                    Sift more than 400 investment trusts to make your search easy.
                </p>
                <p className="screener-header-text">
                    Searching for an investment trust? Home in on the right one for you using our screener. You 
                    can filter our full range of trusts by performance, size, risk rating and more. Or, to find 
                    an investment trust picked out by us, browse our Investment trust select list.
                </p>
            </div>;
    }
}

let screenerLoadingTimeout = 0;

export default function Funds({ instrumentData }) {

    const router = useRouter();
    const { pathname, query } = router;
    let breadcrumb = '';
    let type = '';
    let title = '';
    let description = '';

    const [initialised, setInitialised] = useState(false);
    const [screenerLoaded, setScreenerLoaded] = useState(false);
    const [screenerState, setScreenerState] = useState(null);
    const [presetFilters, setPresetFilters] = useState(null);
    const [isScreenerLoading, setIsScreenerLoading] = useState(true);
    const [numberOfResults, setNumberOfResults] = useState(0);
    const [autoCompleteItems, setAutoCompleteItems] = useState([]);

    useEffect(() => {
        const screenerComponentState = sessionStorage.getItem(screenerStateSessionKey);
        if (screenerComponentState) {
            const screenerStateSession = JSON.parse(screenerComponentState);
            sessionStorage.removeItem(screenerStateSessionKey);
            const presetFiltersUpdate = screenerStateSession?.preselectedFilterValue || [];
            if (Array.isArray(presetFiltersUpdate) && presetFiltersUpdate.length) {
                setPresetFilters(presetFiltersUpdate);
            }
        }
        setTimeout(() => setInitialised(true), 50);
    }, []);

    const updateScreenerState = (state) => {
        clearTimeout(screenerLoadingTimeout);
        screenerLoadingTimeout = setTimeout(() => {
            setScreenerState(state);
            setIsScreenerLoading(false);
            setTimeout(() => setScreenerLoaded(true), 50);
        }, 50);
    }

    if (pathname.includes(FUNDS)) {
        title = 'Fund Screener | Compare UK, Global & Favourite Funds';
        description = 'Find and filter over 3,700 funds by sector, provider, performance, yield and more with our free fund screener, with ratings from Morningstar.';
        breadcrumb = 'Fund screener';
        type = FUND;
    } else if (pathname.includes(ETFS)) {
        title = 'ETF Screener | Compare US, UK & Global ETFs';
        description = 'Compare 3,400+ exchange-traded funds by sector, geographical region, fund provider and more using our free ETF screener, with ratings from Morningstar.';
        breadcrumb = 'ETF screener';
        type = ETF;
    } else if (pathname.includes(SHARES)) {
        title = 'Stock Screener | Compare UK, US & Global Shares';
        description = 'Find, filter and compare more than 14,000 stocks from across the world using our free share screener tool.';
        breadcrumb = 'Shares screener';
        type = EQUITY;
    } else if (pathname.includes(TRUSTS)) {
        title = 'Investment Trust Screener | Comparison Tool';
        description = 'Compare over 400 investment trusts available on our platform by price, performance, size and more using our free screener tool, with ratings from Morningstar.';
        breadcrumb = 'Investment Trust screener';
        type = CEF;
    }

    const mapScreenerPaths = (screenerType) => {
        if (pathname.includes(FUNDS)) {
            return 'Funds';
        } else if (pathname.includes(ETFS)) {
            return 'ETF';
        } else if (pathname.includes(SHARES)) {
            return 'Shares';
        } else if (pathname.includes(TRUSTS)) {
            return 'Investment Trust';
        }
    }

    return <Main title={`${title} | AJ Bell`} description={description} nav={instrumentData.nav} breadcrumb={breadcrumb} page='screener' disableGrey={true}>
        <div className="ajb-grid-row padding-bottom-20">

            <div className='screener-header-card'>
                {displayTopBox(screenerState?.ScreenerAPIFetchState.screenerType)}
            </div>

            <div className="screener-filter-container">
                {initialised &&
                <Screener
                    hasScreenTypeHeaders={true}
                    ScreenType={mapScreenerPaths()}
                    isLoading={isScreenerLoading}
                    filterAPI={instrumentData.filters}
                    presetFilters={presetFilters}
                    optionalHeader={screenerHeaderTabs}
                    className="ajb-col-12 margin-bottom-30"
                    onStateChange={updateScreenerState}
                    searchCTA={`Search ${numberOfResults?.toLocaleString() ?? 0} Result${numberOfResults !== 1 ? 's' : ''}`}
                    onClick={() => {
                        const targetElement = document.getElementById("results-table");
                        if (targetElement) {
                            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }}
                />}
            </div>

            <div className="px-4 ajb-col-12">
                <div id="results-table">
                    {screenerLoaded &&
                    <ResultsTable setAutoCompleteItems={setAutoCompleteItems} setNumberOfResults={setNumberOfResults}  {...instrumentData} type={type} query={query} screenerState={screenerState} />}
                </div>
            </div>
        </div>
    </Main>
}

export async function getServerSideProps(context) {
    try {
        const nav = await YouInvest.getNavigation();

        const filters = await (new MorningStar).getFiltersResponse();

        const instrumentData = { ...nav, filters };

        instrumentData.favourites = await YouInvest.getFavouriteFunds();

        instrumentData.settings = await YouInvest.getSettings();

        sendCacheHeaders(context);

        return { props: { instrumentData } }

    } catch (e) {
        Sentry.captureException(e);
        return {
            notFound: true
        }
    }
}